import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { IEnum_Content_Contenttype } from 'src/graphql/generated/hooks'
import { getContentWriterInformation } from 'src/hooks/useContentWriterInformation'
import { formatNumericDisplayDate } from 'src/utils/formatNumericDisplayDate'
import { normalizeWritersList } from 'src/utils/normalizeContentWriters'

import { Avatar } from '../global/Avatar/Avatar'
import { HtmlContentViewer } from '../global/HtmlContentViewer/HtmlContentViewer'
import { PremiumContentIcon } from '../global/PremiumContentIcon/PremiumContentIcon'
import { InsightCardAuthorMore } from './InsightCardAuthorMore'
import { InsightCardTags } from './InsightCardTags'
interface InsightCardsProps {
  className?: string
  insightItem: any
  key?: number | string
  openTooltipCardId?: string | null
  setOpenTooltipCardId: (id: string | null) => void
  cardId: string
  contentType: IEnum_Content_Contenttype
}

export const InsightCards: React.FC<InsightCardsProps> = ({
  insightItem,
  cardId,
  openTooltipCardId,
  setOpenTooltipCardId,
  contentType,
}) => {
  const insightWriters = normalizeWritersList(insightItem?.writers?.length ? insightItem.writers : insightItem?.writer)
  const firstInfo = insightWriters[0] ? getContentWriterInformation(insightWriters[0]) : null
  const secondWriterInfo = insightWriters[1] ? getContentWriterInformation(insightWriters[1]) : null
  let contentTags = ''

  if (contentType === IEnum_Content_Contenttype.KnowHow) {
    contentTags = [insightItem?.subjectMatters].filter((str) => str && str.trim() !== '').join(', ')
  } else {
    contentTags = [insightItem?.subjectMatters, insightItem?.themes, insightItem?.vendorTopics]
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  }

  const makeLinks = (
    linkTo: string,
    subjectMatterArray: string | undefined,
    subjectMatterIDs: number[] | undefined,
  ) => {
    let goto = 'subject_matter_'
    if (!subjectMatterArray || subjectMatterArray === '' || subjectMatterArray === undefined) {
      return []
    }
    if (linkTo === 'subject_matter') {
      goto = 'know-how/?subject=subject_matter_'
    } else if (linkTo === 'theme') {
      goto = 'legaltech-resources-for-lawyers/?subject=theme_'
    } else if (linkTo === 'vendor_topic') {
      goto = 'resources-for-solution-providers/?vendorTopic='
    }

    return subjectMatterArray?.split(',').map((name: string, index: number) => ({
      name: name.trim(),
      id: subjectMatterIDs?.[index],
      link: `${goto}${subjectMatterIDs?.[index]}`,
    }))
  }

  const subjectMatterLiks = makeLinks('subject_matter', insightItem?.subjectMatters, insightItem?.subjectMatterIds)
  const themesLiks = makeLinks('theme', insightItem?.themes, insightItem?.themeIds)
  const vendorTopicsLiks = makeLinks('vendor_topic', insightItem?.vendorTopics, insightItem?.vendorTopicIds)

  let contentTagsLink = []
  if (contentType === IEnum_Content_Contenttype.KnowHow) {
    contentTagsLink = subjectMatterLiks
  } else {
    contentTagsLink = [subjectMatterLiks, themesLiks, vendorTopicsLiks].flatMap((item) => item)
  }

  return (
    <div className="insight-cards rounded-lg pl-[24px] pt-[24px] pr-[24px] pb-[23px]">
      <Link prefetch={false} href={`/contents/${insightItem?.slug}`} passHref>
        <div className="avtar-div  rounded-lg mb-[12px] relative">
          {insightItem?.defaultImage ? (
            <Image
              src={insightItem?.defaultImage}
              alt=""
              className="rounded-lg object-fill w-full"
              priority={true}
              layout="fill"
              quality={90}
            />
          ) : (
            <PremiumContentIcon
              contentType={insightItem?.contentType}
              size="full"
              defaultImage={insightItem?.defaultImage}
            />
          )}
        </div>
        {insightItem?.title && (
          <h3 title={insightItem?.title} className="title line-clamp-2 mb-[8px] h-[54px]">
            {insightItem?.title}
          </h3>
        )}
      </Link>
      <div className="insight-conatiner flex flex-wrap gap-2 mb-[9px] h-[22px]">
        {contentTags && (
          <InsightCardTags
            key={`${cardId}-InsightCards-desktop`}
            cardId={cardId}
            tagItems={contentTags}
            contentTagsLink={contentTagsLink}
            openTooltipCardId={openTooltipCardId}
            setOpenTooltipCardId={setOpenTooltipCardId}
          />
        )}
      </div>
      <div className="insight-mobile-conatiner flex flex-wrap gap-2 mb-[9px] h-[22px]">
        {contentTags && (
          <InsightCardTags
            key={`${cardId}-InsightCards-mobile`}
            cardId={cardId}
            tagItems={contentTags}
            contentTagsLink={contentTagsLink}
            openTooltipCardId={openTooltipCardId}
            setOpenTooltipCardId={setOpenTooltipCardId}
            isMobile={true}
          />
        )}
      </div>
      <div className="text-sm mb-[8px] font-semibold text-gray-800 flex min-h-[24px] flex-wrap items-center gap-x-0">
        {firstInfo?.name ? (
          <>
            <Link prefetch={false} href={`/contents/${insightItem?.slug}`} className="flex items-center gap-x-2">
              <Avatar
                size="24px"
                initialsTextSize="12px"
                url={firstInfo.originalAvatar || firstInfo.logo || ''}
                name={firstInfo.name}
                defaultMode="initials"
                border={firstInfo.originalAvatar ? 'circle' : 'no-border'}
                variant="turquoise"
                className={'border-none'}
              />

              <span className="writer-name">{firstInfo.name}</span>
            </Link>
            {secondWriterInfo ? (
              <InsightCardAuthorMore
                insightCardId={String(insightItem?.id)}
                secondWriter={secondWriterInfo}
                openTooltipCardId={openTooltipCardId}
                setOpenTooltipCardId={setOpenTooltipCardId}
              />
            ) : null}
          </>
        ) : (
          <div className="text-sm mb-[8px] font-semibold text-gray-800 flex items-center space-x-2 h-[24px]" />
        )}
      </div>
      <Link prefetch={false} href={`/contents/${insightItem?.slug}`} passHref>
        <p className="published mb-[4px] h-[22px]">
          {insightItem?.publishedTime ? `Published ${formatNumericDisplayDate(insightItem.publishedTime)}` : ''}
        </p>
        <HtmlContentViewer
          className="description line-clamp-3 overflow-hidden html-show w-full"
          html={insightItem?.snippet || ''}
        />
      </Link>
    </div>
  )
}
